<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import './epp-shared.css';

const { t } = useI18n({ useScope: 'global' });

const MODULE_OPTIONS = [
  {
    id: 'click_and_collect',
    icon: 'pi-shopping-cart',
    labelKey: 'commerceDetail.modulesCnc',
  },
  {
    id: 'pos_register',
    icon: 'pi-calculator',
    labelKey: 'commerceDetail.modulesCaisse',
  },
  {
    id: 'pds_display',
    icon: 'pi-desktop',
    labelKey: 'commerceDetail.modulesPds',
  },
  { id: 'kiosk', icon: 'pi-tablet', labelKey: 'commerceDetail.modulesKiosk' },
] as const;

export interface Category {
  id: string;
  designation: any;
  description?: any;
}

const props = defineProps<{
  categories: Category[];
  loadingCats: boolean;
  selectedCats: string[];
  showCatForm: boolean;
  editCatId: string | null;
  catFormLang: 'fr' | 'en' | 'nl';
  catNameFr: string;
  catNameEn: string;
  catNameNl: string;
  catDescFr: string;
  catDescEn: string;
  catDescNl: string;
  catModules: string[];
  catSaving: boolean;
  catError: string | null;
  showCatNameFrError: boolean;
  transNameCat: boolean;
  transDescCat: boolean;
  deletingCatId: string | null;
  catLabel: (c: Category) => string;
}>();

const emit = defineEmits<{
  'update:catFormLang': [v: 'fr' | 'en' | 'nl'];
  'update:catNameFr': [v: string];
  'update:catNameEn': [v: string];
  'update:catNameNl': [v: string];
  'update:catDescFr': [v: string];
  'update:catDescEn': [v: string];
  'update:catDescNl': [v: string];
  'update:catModules': [v: string[]];
  openNewCat: [];
  openEditCat: [cat: Category];
  closeCatForm: [];
  saveCat: [];
  deleteCat: [cat: Category];
  toggleCat: [id: string];
  catNameBlur: [lang: 'fr' | 'en' | 'nl'];
  catDescBlur: [lang: 'fr' | 'en' | 'nl'];
}>();

function toggleModule(id: string) {
  const idx = props.catModules.indexOf(id);
  const next = [...props.catModules];
  if (idx >= 0) next.splice(idx, 1);
  else next.push(id);
  emit('update:catModules', next);
}
</script>

<template>
  <div>
    <button
      class="epp__add-btn"
      :disabled="showCatForm"
      @click="emit('openNewCat')"
    >
      <i class="pi pi-plus" /> {{ t('product.newCategory') }}
    </button>

    <div v-if="showCatForm" class="epp__cat-form">
      <p class="epp__cat-form-title">
        {{
          editCatId
            ? t('product.editCategoryTitle')
            : t('product.newCategoryTitle')
        }}
      </p>
      <div class="epp__translabel-row">
        <label class="epp__label">{{ t('product.inputLang') }}</label>
        <div class="epp__select-wrap" style="max-width: 10rem">
          <select
            :value="catFormLang"
            class="epp__select epp__select--sm"
            @change="
              emit(
                'update:catFormLang',
                ($event.target as HTMLSelectElement).value as
                  'fr' | 'en' | 'nl',
              )
            "
          >
            <option value="fr">Français</option>
            <option value="en">English</option>
            <option value="nl">Nederlands</option>
          </select>
          <i class="pi pi-chevron-down epp__select-icon" />
        </div>
      </div>
      <div class="epp__cat-form-label">
        <span class="epp__label"
          >{{ t('product.catDesignation') }}
          <span class="epp__req">*</span></span
        >
        <span v-if="transNameCat" class="epp__translating-dot" />
      </div>
      <div class="epp__field-row epp__field-row--3">
        <div class="epp__field">
          <label class="epp__label"
            ><span
              class="epp__lang-badge"
              :class="catFormLang === 'fr' ? 'epp__lang-badge--active' : ''"
              >FR</span
            ></label
          >
          <input
            :value="catNameFr"
            class="epp__input"
            :class="[
              catFormLang === 'fr' ? 'epp__input--primary' : '',
              { 'epp__input--error': showCatNameFrError },
            ]"
            type="text"
            :placeholder="t('product.nameFrPlaceholder')"
            @input="
              emit(
                'update:catNameFr',
                ($event.target as HTMLInputElement).value,
              )
            "
            @blur="emit('catNameBlur', 'fr')"
          />
          <p v-if="showCatNameFrError" class="epp__field-error">
            {{ t('product.requiredField') }}
          </p>
        </div>
        <div class="epp__field">
          <label class="epp__label"
            ><span
              class="epp__lang-badge"
              :class="catFormLang === 'en' ? 'epp__lang-badge--active' : ''"
              >EN</span
            ></label
          >
          <input
            :value="catNameEn"
            class="epp__input"
            :class="catFormLang === 'en' ? 'epp__input--primary' : ''"
            type="text"
            placeholder="Name in English…"
            @input="
              emit(
                'update:catNameEn',
                ($event.target as HTMLInputElement).value,
              )
            "
            @blur="emit('catNameBlur', 'en')"
          />
        </div>
        <div class="epp__field">
          <label class="epp__label"
            ><span
              class="epp__lang-badge"
              :class="catFormLang === 'nl' ? 'epp__lang-badge--active' : ''"
              >NL</span
            ></label
          >
          <input
            :value="catNameNl"
            class="epp__input"
            :class="catFormLang === 'nl' ? 'epp__input--primary' : ''"
            type="text"
            placeholder="Naam…"
            @input="
              emit(
                'update:catNameNl',
                ($event.target as HTMLInputElement).value,
              )
            "
            @blur="emit('catNameBlur', 'nl')"
          />
        </div>
      </div>
      <div class="epp__cat-form-label" style="margin-top: 0.5rem">
        <span class="epp__label">Description (optionnel)</span>
        <span v-if="transDescCat" class="epp__translating-dot" />
      </div>
      <div class="epp__field-row epp__field-row--3">
        <div class="epp__field">
          <label class="epp__label"
            ><span
              class="epp__lang-badge"
              :class="catFormLang === 'fr' ? 'epp__lang-badge--active' : ''"
              >FR</span
            ></label
          >
          <input
            :value="catDescFr"
            class="epp__input"
            :class="catFormLang === 'fr' ? 'epp__input--primary' : ''"
            type="text"
            :placeholder="t('product.descFrPlaceholder')"
            @input="
              emit(
                'update:catDescFr',
                ($event.target as HTMLInputElement).value,
              )
            "
            @blur="emit('catDescBlur', 'fr')"
          />
        </div>
        <div class="epp__field">
          <label class="epp__label"
            ><span
              class="epp__lang-badge"
              :class="catFormLang === 'en' ? 'epp__lang-badge--active' : ''"
              >EN</span
            ></label
          >
          <input
            :value="catDescEn"
            class="epp__input"
            :class="catFormLang === 'en' ? 'epp__input--primary' : ''"
            type="text"
            placeholder="Description EN…"
            @input="
              emit(
                'update:catDescEn',
                ($event.target as HTMLInputElement).value,
              )
            "
            @blur="emit('catDescBlur', 'en')"
          />
        </div>
        <div class="epp__field">
          <label class="epp__label"
            ><span
              class="epp__lang-badge"
              :class="catFormLang === 'nl' ? 'epp__lang-badge--active' : ''"
              >NL</span
            ></label
          >
          <input
            :value="catDescNl"
            class="epp__input"
            :class="catFormLang === 'nl' ? 'epp__input--primary' : ''"
            type="text"
            placeholder="Beschrijving…"
            @input="
              emit(
                'update:catDescNl',
                ($event.target as HTMLInputElement).value,
              )
            "
            @blur="emit('catDescBlur', 'nl')"
          />
        </div>
      </div>

      <div class="epp__cat-form-label" style="margin-top: 0.5rem">
        <span class="epp__label">{{ t('allCollections.modulesLabel') }}</span>
      </div>
      <div class="epp__cat-modules">
        <button
          v-for="m in MODULE_OPTIONS"
          :key="m.id"
          type="button"
          class="epp__cat-module-chip"
          :class="{ 'epp__cat-module-chip--on': catModules.includes(m.id) }"
          @click="toggleModule(m.id)"
        >
          <i :class="['pi', m.icon]" />
          {{ t(m.labelKey) }}
        </button>
      </div>

      <div
        v-if="catError"
        class="epp__error-banner"
        style="margin-top: 0.25rem"
      >
        <i class="pi pi-exclamation-circle" /> {{ catError }}
      </div>
      <div class="epp__cat-form-actions">
        <button
          class="epp__btn epp__btn--ghost"
          :disabled="catSaving"
          @click="emit('closeCatForm')"
        >
          <i class="pi pi-times" /> {{ t('product.cancel') }}
        </button>
        <button
          class="epp__btn epp__btn--primary"
          :disabled="catSaving || !catNameFr.trim()"
          @click="emit('saveCat')"
        >
          <span v-if="catSaving" class="epp__spinner epp__spinner--white" />
          <i v-else class="pi pi-check" />
          {{
            catSaving
              ? t('product.saving')
              : editCatId
                ? t('product.edit')
                : t('product.create')
          }}
        </button>
      </div>
    </div>

    <div v-if="loadingCats" class="epp__cats-loading">
      <span class="epp__spinner" /> {{ t('product.loading') }}
    </div>
    <div v-else-if="categories.length" class="epp__cat-list">
      <div
        v-for="cat in categories"
        :key="cat.id"
        class="epp__cat-row"
        :class="{ 'epp__cat-row--selected': selectedCats.includes(cat.id) }"
      >
        <button class="epp__cat-select" @click="emit('toggleCat', cat.id)">
          <span
            class="epp__cat-check"
            :class="{ 'epp__cat-check--active': selectedCats.includes(cat.id) }"
          >
            <i v-if="selectedCats.includes(cat.id)" class="pi pi-check" />
          </span>
          <span class="epp__cat-name">{{ catLabel(cat) }}</span>
        </button>
        <div class="epp__cat-actions">
          <button
            class="epp__icon-btn"
            :disabled="showCatForm"
            :title="t('product.edit')"
            @click.stop="emit('openEditCat', cat)"
          >
            <i class="pi pi-pencil" />
          </button>
          <button
            class="epp__icon-btn epp__icon-btn--danger"
            :disabled="deletingCatId === cat.id"
            :title="t('product.delete')"
            @click.stop="emit('deleteCat', cat)"
          >
            <span v-if="deletingCatId === cat.id" class="epp__spinner" />
            <i v-else class="pi pi-trash" />
          </button>
        </div>
      </div>
    </div>
    <p v-else-if="!showCatForm" class="epp__cats-empty">
      {{ t('product.noCategoriesHint') }}
    </p>
  </div>
</template>
